import { Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Artist,
  Maybe,
  RankingPeriodType,
  RankingsConnectionFragment,
  RankingUserWithoutIdConnectionFragment,
  User,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  RankingUserDataListItem,
  RankingUserDataListItemSkeleton,
} from '@/components/organisms'
import { ScrollFetcher, Segment } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  user: Maybe<Pick<User, 'id' | 'displayName' | 'avatar'>>
  artist: Maybe<Pick<Artist, 'id' | 'displayName'>>
  myRanking: Maybe<RankingUserWithoutIdConnectionFragment>
  rankings: Maybe<RankingsConnectionFragment>
  periodType: Maybe<RankingPeriodType>
  period?: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: (periodType: RankingPeriodType) => Promise<void>
  onChangePeriod: (periodType: RankingPeriodType) => Promise<void>
}

export const ArtistRankingTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onChangePeriod,
}) => {
  const { t } = useTranslation('artistRanking')
  const { onClick } = useBrowserLink(getHelpUrl('ranking'))
  const { routerPages, router, generatePath } = useRouter<{
    artistId: string
  }>()
  const [currentSegment, setCurrentSegment] = useState<number>(0)

  const handleChangeSegment = useCallback(
    async (segment: number) => {
      setCurrentSegment(segment)
      await onChangePeriod(
        segment === 0 ? RankingPeriodType.Monthly : RankingPeriodType.Yearly
      )
    },
    [onChangePeriod]
  )

  return (
    <>
      <Head title={t('title', { displayName: state.artist?.displayName })} />
      <BaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
          title: t('title', { displayName: state.artist?.displayName }),
          right: (
            <IconButton.Help
              edge="end"
              color="primary.main"
              onClick={onClick}
            />
          ),
        }}
      >
        <Segment
          labels={[t('month'), t('total')]}
          value={currentSegment}
          disableAnimateTransitions
          onChange={handleChangeSegment}
        >
          <RankingsCell
            loading={state.loading}
            refetching={state.refetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={() =>
              onFetchMore(
                currentSegment === 0
                  ? RankingPeriodType.Monthly
                  : RankingPeriodType.Yearly
              )
            }
            user={state.user}
            rankings={
              state.periodType === RankingPeriodType.Monthly
                ? state.rankings
                : null
            }
            myRanking={state.myRanking}
            note={
              state.period
                ? t('aggregationPeriod', { period: state.period })
                : undefined
            }
          />
          <RankingsCell
            loading={state.loading}
            refetching={state.refetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={() =>
              onFetchMore(
                currentSegment === 0
                  ? RankingPeriodType.Monthly
                  : RankingPeriodType.Yearly
              )
            }
            user={state.user}
            rankings={
              state.periodType === RankingPeriodType.Yearly
                ? state.rankings
                : null
            }
            myRanking={state.myRanking}
          />
        </Segment>
      </BaseLayout>
    </>
  )
}

type RankingsCellProps = {
  user: State['user']
  rankings: State['rankings']
  myRanking: State['myRanking']
  loading: State['loading']
  refetching: State['refetching']
  hasNextPage: State['hasNextPage']
  onFetchMore: () => Promise<void>
  note?: string
}
const RankingsCell: Cell<RankingsCellProps> = ({
  user,
  rankings,
  myRanking,
  loading,
  refetching,
  hasNextPage,
  onFetchMore,
  note,
}) => {
  const { t } = useTranslation('artistRanking')
  return (
    <CellFrame sx={{ pb: 4 }}>
      <Stack spacing={2} sx={{ pb: 4 }}>
        {!!note && (
          <Typography variant="caption" color="text.hint">
            {note}
          </Typography>
        )}
        <Typography variant="h6" color="text.secondary">
          {t('currentStanding')}
        </Typography>
        {(myRanking || user) && !loading ? (
          <RankingUserDataListItem
            rankingUser={myRanking}
            user={user}
            clickable={false}
          />
        ) : (
          <RankingUserDataListItemSkeleton />
        )}
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h6" color="text.secondary">
          {t('top10')}
        </Typography>
        <Stack>
          <ScrollFetcher
            nodes={rankings?.edges.map(({ node }) => (
              <RankingUserDataListItem
                key={node.id}
                rankingUser={node}
                user={null}
                clickable={false}
              />
            ))}
            componentProps={{ spacing: 3 }}
            loading={loading || refetching}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            loadingComponent={
              <Stack spacing={3}>
                {Array.from(Array(4).keys()).map(index => (
                  <RankingUserDataListItemSkeleton key={index} />
                ))}
              </Stack>
            }
          />
        </Stack>
      </Stack>
    </CellFrame>
  )
}
